import { FC, memo, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import LicenseContext from 'src/contexts/LicenseContext';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';
import { DateModel } from 'src/models/DateModel';
import Component from 'src/pages/TruckShifts/Component';
import { buildTruckShiftsPath, canonicalRangeFromUrl, isPeriodMode, PeriodMode, PERIOD_STORAGE_KEY } from 'src/pages/TruckShifts/truckShiftGrid';

const TruckShifts: FC = memo(() => {
  const { startOn, endOn, backTo } = useParams();
  const context = useContext(LicenseContext);
  const navigate = useNavigate();
  // 週/月 表示モードをセッション跨ぎで永続化。範囲未指定の初回入場時の既定に使う。
  const [preferredPeriod, setPreferredPeriod] = usePersistedJSON<PeriodMode>(PERIOD_STORAGE_KEY, 'week', isPeriodMode);

  const on = context.config?.on;
  const startValid = !!startOn && new DateModel(startOn).isValid();

  // URL 範囲は「呼び出し元（配車画面）の範囲＝戻り範囲」としてそのまま保持する（/shifts と同じ）。
  // 週/月へ正規化した表示範囲は Component 内で startOn を anchor に導出する。ここでは表示モード(period)だけ決める。
  // period は範囲明示 URL からは URL 由来（共有リンクを壊さない）、未指定は永続化した preferredPeriod。
  const { period } = on
    ? canonicalRangeFromUrl(startOn, endOn, on, preferredPeriod)
    : { period: preferredPeriod };

  useEffect(() => {
    if (!on) return;
    // 不正・未指定 URL のみ config.on の単日へ寄せて redirect（戻り範囲を必ず妥当な日付にする）。妥当な範囲は保持。
    if (!startValid) {
      void navigate(buildTruckShiftsPath({ startOn: on, endOn: on, backTo }), { replace: true });
    }
  }, [on, startValid, backTo, navigate]);

  // 表示モードが確定・変化したら永続化（次回の初回既定に反映）。
  useEffect(() => {
    if (on && startValid) setPreferredPeriod(period);
  }, [on, startValid, period, setPreferredPeriod]);

  if (!on || !startValid || !startOn) return null;

  const backEndOn = endOn && new DateModel(endOn).isValid() ? endOn : startOn;

  return (
    <Component
      initialStartOn={startOn}
      initialEndOn={backEndOn}
      initialPeriod={period}
      backTo={backTo}
      onPeriodPersist={setPreferredPeriod}
    />
  );
});

export default TruckShifts;
